// components/branches/config/appointment/AppointmentTypesSettings.jsx
import React from "react";
import NumberInput from "../../../common/NumberInput";

const AppointmentTypesSettings = ({ types, onChange, isEditMode }) => {
  const APPOINTMENT_TYPE_OPTIONS = [
    {
      value: "PRODUCT_CONSULTATION",
      label: "Product Consultation",
      duration: 30,
    },
    { value: "CUSTOM_DESIGN", label: "Custom Design", duration: 60 },
    {
      value: "REPAIR_CONSULTATION",
      label: "Repair Consultation",
      duration: 30,
    },
    { value: "APPRAISAL", label: "Appraisal", duration: 45 },
    { value: "ENGAGEMENT_RING", label: "Engagement Ring", duration: 60 },
    { value: "WEDDING_JEWELRY", label: "Wedding Jewelry", duration: 60 },
    { value: "GENERAL_INQUIRY", label: "General Inquiry", duration: 15 },
    { value: "VIP_CLIENT", label: "VIP Client", duration: 90 },
    { value: "COLLECTION_VIEWING", label: "Collection Viewing", duration: 45 },
  ];

  const toggleType = (typeValue) => {
    const existing = types.find((t) => t.type === typeValue);
    if (existing) {
      onChange(types.filter((t) => t.type !== typeValue));
    } else {
      const template = APPOINTMENT_TYPE_OPTIONS.find(
        (t) => t.value === typeValue
      );
      onChange([
        ...types,
        {
          type: typeValue,
          enabled: true,
          duration: template.duration,
          requiresDeposit: false,
          depositAmount: 0,
        },
      ]);
    }
  };

  const updateTypeField = (typeValue, field, value) => {
    const updatedTypes = types.map((t) => {
      if (t.type === typeValue) {
        return { ...t, [field]: value };
      }
      return t;
    });
    onChange(updatedTypes);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Appointment Types</h3>
      <p className="text-gray-600">
        Configure which types of appointments are available at this branch
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {APPOINTMENT_TYPE_OPTIONS.map((option) => {
          const existingType = types.find((t) => t.type === option.value);

          return (
            <div
              key={option.value}
              className={`border-2 rounded-lg p-4 transition-all ${
                existingType
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium">{option.label}</h4>
                  <p className="text-sm text-gray-600">
                    Duration: {existingType?.duration || option.duration} min
                  </p>
                </div>
                {isEditMode && (
                  <input
                    type="checkbox"
                    checked={!!existingType}
                    onChange={() => toggleType(option.value)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                )}
              </div>

              {existingType && (
                <div className="space-y-3 pt-3 border-t">
                  {/* Duration Override */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes)
                    </label>
                    {isEditMode ? (
                      <NumberInput
                        value={existingType.duration || option.duration}
                        onChange={(value) =>
                          updateTypeField(option.value, "duration", value)
                        }
                        step="15"
                        placeholder="Enter duration"
                        className="text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-sm font-medium">
                        {existingType.duration || option.duration} minutes
                      </p>
                    )}
                  </div>

                  {/* Requires Deposit */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`deposit-${option.value}`}
                      checked={existingType.requiresDeposit || false}
                      onChange={(e) =>
                        updateTypeField(
                          option.value,
                          "requiresDeposit",
                          e.target.checked
                        )
                      }
                      disabled={!isEditMode}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`deposit-${option.value}`}
                      className="text-sm"
                    >
                      Requires Deposit
                    </label>
                  </div>

                  {/* Deposit Amount */}
                  {existingType.requiresDeposit && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Deposit Amount (₹)
                      </label>
                      {isEditMode ? (
                        <NumberInput
                          value={existingType.depositAmount || 0}
                          onChange={(value) =>
                            updateTypeField(
                              option.value,
                              "depositAmount",
                              value
                            )
                          }
                          step="100"
                          placeholder="Enter deposit amount"
                          prefix="₹"
                          className="text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-sm font-medium">
                          ₹
                          {(existingType.depositAmount || 0).toLocaleString(
                            "en-IN"
                          )}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Refundable Checkbox */}
                  {existingType.requiresDeposit && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`refundable-${option.value}`}
                        checked={existingType.depositRefundable !== false}
                        onChange={(e) =>
                          updateTypeField(
                            option.value,
                            "depositRefundable",
                            e.target.checked
                          )
                        }
                        disabled={!isEditMode}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`refundable-${option.value}`}
                        className="text-sm"
                      >
                        Deposit is Refundable
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-blue-700">
            <strong>{types.length}</strong> appointment type(s) enabled
          </p>
          {types.filter((t) => t.requiresDeposit).length > 0 && (
            <p className="text-sm text-blue-700">
              <strong>{types.filter((t) => t.requiresDeposit).length}</strong>{" "}
              require(s) deposit
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentTypesSettings;
